/* Wavex cart presentation only. Native Dawn owns all cart mutations and focus. */
(function () {
  'use strict';

  const THRESHOLD = 7501;
  const SHIPPING_FEE = 2000;

  function readCart(input) {
    const total = Number(input.total);
    const itemCount = Number(input.itemCount);
    const valid = Number.isSafeInteger(total) && total >= 0 &&
      Number.isSafeInteger(itemCount) && itemCount >= 0;
    const currency = String(input.currency || '').toUpperCase();
    const requiresShipping = input.requiresShipping === true || input.requiresShipping === 'true';
    const empty = itemCount === 0;
    const eligible = valid && currency === 'USD' && requiresShipping && !empty;
    const qualified = eligible && total >= THRESHOLD;
    const progress = eligible ? Math.min(total / THRESHOLD, 1) : 0;
    const milestone = !eligible ? 0 : qualified ? 3 : progress >= 0.8 ? 2 : progress >= 0.5 ? 1 : 0;
    const shipping = !valid || empty ? null : !requiresShipping ? 0 :
      currency !== 'USD' ? null : qualified ? 0 : SHIPPING_FEE;
    return {
      total, itemCount, currency, requiresShipping, valid, empty, eligible, qualified,
      progress, milestone, remaining: eligible ? Math.max(THRESHOLD - total, 0) : null,
      shipping, estimatedTotal: shipping === null ? null : total + shipping,
    };
  }

  function transition(previous, current, reducedMotion) {
    // First server render establishes the baseline. Already-qualified carts do
    // not celebrate on page load. A currency switch is not a shopping milestone.
    const comparable = previous && previous.valid && current.valid &&
      previous.currency === current.currency &&
      (previous.requiresShipping === current.requiresShipping || previous.empty);
    const advanced = Boolean(comparable && current.eligible &&
      current.total > previous.total && current.milestone > previous.milestone);
    return {
      animate: advanced && !reducedMotion,
      celebration: advanced ? current.qualified ? 'free' : 'progress' : null,
      fromProgress: comparable ? previous.progress : current.progress,
    };
  }

  // Pure logic is exportable for deterministic tests; no browser dependency.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { THRESHOLD, SHIPPING_FEE, readCart, transition };
  }
  if (typeof window === 'undefined' || !window.customElements ||
      window.customElements.get('wavex-cart-summary')) return;

  // Dawn retains <cart-drawer> while replacing #CartDrawer, .drawer__inner or
  // .cart-drawer__footer. A WeakMap on that stable host survives every path.
  // No listeners on native controls, fetch interception, or extra API requests.
  const drawerStates = new WeakMap();

  class WavexCartSummary extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        const drawer = this.closest('cart-drawer');
        if (!drawer) return;
        const current = readCart(this.dataset);
        const previous = drawerStates.get(drawer);
        this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
        const reducedMotion = this.motionPreference.matches;
        const change = transition(previous, current, reducedMotion);
        drawerStates.set(drawer, current);

        if (change.animate && typeof this.animate === 'function') {
          this.onMotionChange = (event) => {
            if (!event.matches) return;
            (this.animations || []).forEach((animation) => animation.cancel());
            delete this.dataset.celebration;
          };
          this.motionPreference.addEventListener('change', this.onMotionChange);
          const fill = this.querySelector('.wx-cart-shipping__fill');
          if (fill) this.trackAnimation(fill.animate([
            { width: `${change.fromProgress * 100}%` },
            { width: `${current.progress * 100}%` },
          ], { duration: 520, easing: 'cubic-bezier(.2,.75,.25,1)' }));

          const icon = this.querySelector('.wx-cart-shipping__icon');
          if (icon) this.trackAnimation(icon.animate([
            { transform: 'scale(1)' },
            { transform: change.celebration === 'free' ? 'scale(1.14)' : 'scale(1.06)', offset: 0.45 },
            { transform: 'scale(1)' },
          ], { duration: 600, easing: 'ease-out' }));

          this.dataset.celebration = change.celebration;
          this.celebrationTimer = window.setTimeout(() => {
            delete this.dataset.celebration;
          }, 900);
        }
      });
    }

    trackAnimation(animation) {
      if (!this.animations) this.animations = [];
      this.animations.push(animation);
    }

    disconnectedCallback() {
      window.clearTimeout(this.celebrationTimer);
      if (this.onMotionChange) {
        this.motionPreference.removeEventListener('change', this.onMotionChange);
      }
      (this.animations || []).forEach((animation) => animation.cancel());
      this.animations = [];
      delete this.dataset.celebration;
    }
  }

  window.customElements.define('wavex-cart-summary', WavexCartSummary);
})();
